import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { scheduleService } from '../../../services/scheduleService';

const RoomManager = ({ conferenceId, rooms, onRefresh }) => {
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`managementDashboard.programTab.rooms.${key}`, opts);
  const u = (key, opts) => t(`managementDashboard.programTab.units.${key}`, opts);

  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', location: '', capacity: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setForm({ name: '', location: '', capacity: '' });
    setShowAdd(false);
    setEditingId(null);
    setError('');
  };

  const handleSave = async () => {
    if (!form?.name?.trim()) {
      setError(k('nameRequired'));
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        name: form?.name?.trim(),
        location: form?.location?.trim() || null,
        capacity: form?.capacity ? parseInt(form?.capacity) : null,
      };
      if (editingId) {
        await scheduleService?.updateRoom(editingId, payload);
      } else {
        await scheduleService?.createRoom(conferenceId, { ...payload, sortOrder: rooms?.length || 0 });
      }
      resetForm();
      onRefresh?.();
    } catch (err) {
      setError(err?.message || k('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (room) => {
    setEditingId(room?.id);
    setForm({
      name: room?.name || '',
      location: room?.location || '',
      capacity: room?.capacity?.toString() || '',
    });
    setShowAdd(true);
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm(k('confirmDelete'))) return;
    try {
      await scheduleService?.deleteRoom(roomId);
      onRefresh?.();
    } catch (err) {
      setError(err?.message || k('deleteFailed'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{k('title')}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{k('subtitle')}</p>
        </div>
        {!showAdd && (
          <Button size="sm" variant="outline" iconName="Plus" iconPosition="left" onClick={() => setShowAdd(true)}>
            {k('add')}
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-3 py-2 rounded-md text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={14} />
          {error}
        </div>
      )}

      {showAdd && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">
            {editingId ? k('editTitle') : k('newTitle')}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label={k('name')}
              required
              value={form?.name}
              onChange={(e) => setForm({ ...form, name: e?.target?.value })}
              placeholder={k('namePlaceholder')}
            />
            <Input
              label={k('location')}
              value={form?.location}
              onChange={(e) => setForm({ ...form, location: e?.target?.value })}
              placeholder={k('locationPlaceholder')}
            />
            <Input
              label={k('capacity')}
              type="number"
              value={form?.capacity}
              onChange={(e) => setForm({ ...form, capacity: e?.target?.value })}
              placeholder={k('capacityPlaceholder')}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} loading={saving}>
              {editingId ? k('update') : k('add')}
            </Button>
            <Button size="sm" variant="ghost" onClick={resetForm}>{k('cancel')}</Button>
          </div>
        </div>
      )}

      {rooms?.length === 0 && !showAdd ? (
        <div className="text-center py-8 bg-card border border-border rounded-lg">
          <Icon name="DoorOpen" size={24} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{k('empty')}</p>
          <p className="text-xs text-muted-foreground mt-1">{k('emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rooms?.map((room, idx) => (
            <div
              key={room?.id}
              className="bg-card border border-border rounded-lg px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                  {idx + 1}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{room?.name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {room?.location && <span>{room?.location}</span>}
                    {room?.capacity && (
                      <>
                        {room?.location && <span>&middot;</span>}
                        <span>{u('seats', { count: room?.capacity })}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="xs" variant="ghost" iconName="Pencil" onClick={() => handleEdit(room)} />
                <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => handleDelete(room?.id)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoomManager;
